import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

if (typeof window !== 'undefined') {
  window.history.scrollRestoration = 'manual';
}

const STORAGE_PREFIX = 'scroll_';

export interface ScrollState {
  y: number;
  eventCount?: number;
}

const getScrollY = (): number =>
  Math.max(
    window.scrollY || 0,
    document.documentElement.scrollTop || 0,
    document.body.scrollTop || 0,
  );

const setScrollY = (y: number) => {
  window.scrollTo(0, y);
  document.documentElement.scrollTop = y;
  document.body.scrollTop = y;
};

const getScrollHeight = (): number =>
  Math.max(document.documentElement.scrollHeight, document.body.scrollHeight);

/** Eingaben, die eine laufende Wiederherstellung abbrechen (der Nutzer scrollt selbst). */
const INTERRUPTS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;

export function useScrollRestoration(ready: boolean = true) {
  const location = useLocation();
  // Use pathname+search instead of location.key — iOS Safari assigns a new
  // location.key on swipe-back, breaking key-based lookup. Path is stable.
  const key = STORAGE_PREFIX + location.pathname + location.search;
  const restoringRef = useRef(false);

  const explicitlySavedRef = useRef(false);

  const save = (extra?: Partial<ScrollState>) => {
    const existing = load() ?? {} as Partial<ScrollState>;
    const y = getScrollY();
    const state: ScrollState = { ...existing, y, ...extra };
    sessionStorage.setItem(key, JSON.stringify(state));
    if (extra) explicitlySavedRef.current = true;
  };

  const load = (): ScrollState | null => {
    const raw = sessionStorage.getItem(key);
    if (!raw) return null;
    try { return JSON.parse(raw); } catch { return null; }
  };

  useEffect(() => {
    return () => {
      // Skip cleanup save if we already saved explicitly (via click handler) —
      // by the time unmount runs, iOS may have already started its navigation
      // animation and reset scroll to ~0, which would clobber the correct value.
      if (!restoringRef.current && !explicitlySavedRef.current) save();
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  useEffect(() => {
    if (!ready) return;
    const state = load();
    if (!state || state.y === 0) return;

    restoringRef.current = true;
    const startTime = Date.now();
    const targetY = state.y;
    let frame = 0;

    // Nur den Restore-Lauf abbrechen — OHNE die gespeicherte Position zu verwerfen.
    // Das ist der Unmount-Pfad: React räumt Cleanups in Deklarationsreihenfolge ab, d.h. der
    // Effekt oben hat die Position gerade erst gespeichert. Würde hier removeItem() laufen,
    // löschte dieser Cleanup exakt diesen frischen Wert wieder — die Zurück-Navigation
    // funktionierte dann genau einmal und landete danach immer oben.
    const stopRestoring = () => {
      cancelAnimationFrame(frame);
      restoringRef.current = false;
      INTERRUPTS.forEach(evt => window.removeEventListener(evt, finish));
    };

    // Position ist verbraucht (Restore fertig oder vom Nutzer abgebrochen) → Key freigeben.
    // Nur solange die Seite montiert ist; ein späteres Unmount schreibt ohnehin neu.
    function finish() {
      stopRestoring();
      sessionStorage.removeItem(key);
    }

    // Scrollt der Nutzer selbst, während wir die Position noch nachführen, gewinnt er:
    // sonst zerrt der enforce-Loop bis zu 1,5s lang gegen jede Eingabe zurück auf targetY.
    INTERRUPTS.forEach(evt => window.addEventListener(evt, finish, { passive: true }));

    const enforce = () => {
      const maxScrollable = getScrollHeight() - window.innerHeight;
      const reachable = Math.min(targetY, Math.max(maxScrollable, 0));
      if (reachable > 0 && Math.abs(getScrollY() - reachable) > 2) {
        setScrollY(reachable);
      }
      if (Date.now() - startTime < 1500) {
        frame = requestAnimationFrame(enforce);
      } else {
        finish();
      }
    };

    frame = requestAnimationFrame(enforce);
    return stopRestoring;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready]);

  return { saveScrollState: save, loadScrollState: load };
}
